import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  PlayIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  VideoCameraIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';
import {
  fetchInterviewRequest,
  startInterviewRequest,
  selectSessionData,
  selectIsInterviewStarted,
  selectLoading,
  selectError,
  clearError,
} from '@store/slices/interviewSlice';
import Header from '@components/Layout/Header';
import ErrorAlert from '@components/UI/ErrorAlert';

const STORAGE_KEY_PREFIX = 'interview_start_data_';

/**
 * InterviewReady Page
 * Pre-flight check page shown before the interview API call is made.
 * Fully refreshable — startData is persisted in sessionStorage.
 * Dispatches startInterviewRequest only when user clicks "Start Now".
 * Navigates to /session once isInterviewStarted becomes true.
 */
const InterviewReady = () => {
  const { sessionToken } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sessionData = useSelector(selectSessionData);
  const isInterviewStarted = useSelector(selectIsInterviewStarted);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);

  const storageKey = `${STORAGE_KEY_PREFIX}${sessionToken}`;

  // Load startData from sessionStorage (refresh-resilient)
  const [startData, setStartData] = useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Camera pre-flight state
  const [mediaStream, setMediaStream] = useState(/** @type {MediaStream|null} */ (null));
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(/** @type {string|null} */ (null));
  const [requestingCamera, setRequestingCamera] = useState(false);
  const videoRef = useRef(/** @type {HTMLVideoElement|null} */ (null));

  // Online status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Fetch sessionData on mount if missing (refresh case)
  useEffect(() => {
    if (sessionToken && !sessionData) {
      dispatch(fetchInterviewRequest(sessionToken));
    }
  }, [dispatch, sessionToken, sessionData]);

  // Redirect to intro if no startData found even after sessionStorage check
  useEffect(() => {
    if (!startData) {
      navigate(`/interview/${sessionToken}`, { replace: true });
    }
  }, [startData, navigate, sessionToken]);

  // If interview is already started (e.g. user refreshed after starting), go directly to session
  useEffect(() => {
    if (isInterviewStarted) {
      sessionStorage.removeItem(storageKey);
      navigate(`/interview/${sessionToken}/session`, { replace: true });
    }
  }, [isInterviewStarted, navigate, sessionToken, storageKey]);

  // Request camera access
  useEffect(() => {
    if (!startData) return;
    const isVideo = startData.mediaMode === 'video';
    const isAudio = startData.mediaMode === 'audio';

    if (!isVideo && !isAudio) {
      // Text mode — mark camera as not needed
      setCameraReady(true);
      return;
    }

    const requestMedia = async () => {
      setRequestingCamera(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: isVideo,
          audio: true,
        });
        setMediaStream(stream);
        setCameraReady(true);
        setCameraError(null);
      } catch (err) {
        const error = /** @type {any} */ (err);
        setCameraReady(false);
        let msg = 'Unable to access camera/microphone.';
        if (error.name === 'NotAllowedError') msg = 'Permission denied. Allow camera access in your browser.';
        else if (error.name === 'NotFoundError') msg = 'No camera found. Check your device.';
        else if (error.name === 'NotReadableError') msg = 'Camera is in use by another app.';
        setCameraError(msg);
      } finally {
        setRequestingCamera(false);
      }
    };

    requestMedia();

    return () => {
      // Cleanup on unmount
      setMediaStream(prev => {
        if (prev) /** @type {MediaStream} */ (prev).getTracks().forEach(t => t.stop());
        return null;
      });
    };
  }, [startData]);

  // Attach stream to video element
  useEffect(() => {
    if (videoRef.current && mediaStream) {
      videoRef.current.srcObject = mediaStream;
    }
  }, [mediaStream]);

  // Cleanup stream when navigating away
  useEffect(() => {
    return () => {
      if (mediaStream) mediaStream.getTracks().forEach(t => t.stop());
    };
  }, [mediaStream]);

  const handleStartNow = () => {
    if (!startData || !sessionData) return;
    dispatch(
      startInterviewRequest({
        sessionToken,
        candidateName: sessionData.candidate_name,
        candidateEmail: sessionData.candidate_email,
        termsAccepted: startData.termsAccepted,
        termsVersion: startData.termsVersion,
        recordingConsent: startData.recordingConsent,
        mediaMode: startData.mediaMode,
      })
    );
  };

  const handleGoBack = () => {
    if (mediaStream) mediaStream.getTracks().forEach(t => t.stop());
    navigate(`/interview/${sessionToken}/setup`, { replace: true });
  };

  if (!startData) return null;

  const isVideoMode = startData.mediaMode === 'video';
  const isAudioMode = startData.mediaMode === 'audio';
  const needsMedia = isVideoMode || isAudioMode;

  const canStart = !loading && startData.termsAccepted && startData.recordingConsent && (!needsMedia || cameraReady) && isOnline;

  const checks = [
    {
      label: 'Terms & Conditions accepted',
      ready: startData.termsAccepted,
    },
    {
      label: 'Recording consent given',
      ready: startData.recordingConsent,
    },
    {
      label: isVideoMode ? 'Camera & microphone ready' : isAudioMode ? 'Microphone ready' : 'Text mode selected',
      ready: cameraReady,
      loading: requestingCamera,
      error: cameraError,
    },
    {
      label: 'Internet connection',
      ready: isOnline,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />

      <main className="max-w-2xl mx-auto px-4 py-8 sm:py-12">

        {/* Page heading */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 bg-amber-50 border border-amber-200 px-4 py-2 rounded-full mb-4">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">Once you start, there is no going back</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Ready to Begin?
          </h1>
          <p className="text-gray-600 text-base sm:text-lg max-w-md mx-auto">
            Your session timer will start the moment you click <strong>Start Now</strong>.
            Take a moment to complete the checks below.
          </p>
        </div>

        {/* Camera preview (video mode only) */}
        {isVideoMode && (
          <div className="mb-6 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center space-x-2">
              <VideoCameraIcon className="w-5 h-5 text-indigo-600" />
              <span className="font-semibold text-gray-900">Camera Preview</span>
            </div>
            <div className="p-4">
              {cameraError ? (
                <div className="aspect-video bg-red-50 rounded-xl flex flex-col items-center justify-center border-2 border-red-200">
                  <VideoCameraIcon className="w-10 h-10 text-red-400 mb-2" />
                  <p className="text-sm font-medium text-red-800 mb-1">Camera unavailable</p>
                  <p className="text-xs text-red-600 text-center px-4">{cameraError}</p>
                  <button
                    onClick={() => {
                      setCameraError(null);
                      setCameraReady(false);
                      setRequestingCamera(false);
                    }}
                    className="mt-3 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Try Again
                  </button>
                </div>
              ) : mediaStream ? (
                <div className="relative rounded-xl overflow-hidden bg-gray-900">
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full aspect-video object-cover rounded-xl"
                  />
                  <div className="absolute top-2 left-2 flex items-center space-x-1 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    <span>LIVE</span>
                  </div>
                </div>
              ) : (
                <div className="aspect-video bg-gray-100 rounded-xl flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-3" />
                  <p className="text-sm text-gray-500">Requesting camera access…</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pre-flight checklist */}
        <div className="mb-6 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center space-x-2">
            <ShieldCheckIcon className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-gray-900">Pre-flight Checks</span>
          </div>
          <ul className="divide-y divide-gray-50">
            {checks.map((check, i) => (
              <li key={i} className="flex items-center space-x-4 px-5 py-4">
                {check.loading ? (
                  <div className="w-6 h-6 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin flex-shrink-0" />
                ) : check.error ? (
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full" />
                  </div>
                ) : check.ready ? (
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <div className="w-2.5 h-2.5 bg-gray-400 rounded-full" />
                  </div>
                )}
                <div className="flex-1">
                  <span className={`text-sm font-medium ${check.error ? 'text-red-700' : check.ready ? 'text-gray-900' : 'text-gray-500'}`}>
                    {check.label}
                  </span>
                  {check.error && (
                    <p className="text-xs text-red-600 mt-0.5">{check.error}</p>
                  )}
                </div>
                {check.ready && !check.error && (
                  <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Ready</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Error from API */}
        {error && (
          <div className="mb-6">
            <ErrorAlert message={error} onClose={() => dispatch(clearError())} />
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleGoBack}
            disabled={loading}
            className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:border-gray-300 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Go Back</span>
          </button>

          <button
            onClick={handleStartNow}
            disabled={!canStart}
            className={`flex-1 flex items-center justify-center space-x-3 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 shadow-lg ${
              canStart
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white hover:shadow-xl transform hover:scale-105 active:scale-95'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                <span>Starting…</span>
              </>
            ) : (
              <>
                <PlayIcon className="w-6 h-6" />
                <span>Start Now</span>
              </>
            )}
          </button>
        </div>

        {!canStart && !loading && (
          <p className="text-center text-sm text-amber-600 mt-3">
            {!isOnline && 'You appear to be offline — check your connection.'}
            {isOnline && cameraError && 'Fix the camera issue above before starting.'}
            {isOnline && !cameraError && requestingCamera && 'Waiting for camera permission…'}
          </p>
        )}

        {/* Reassurance footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Your responses are end-to-end encrypted and reviewed only by humans.
        </p>
      </main>
    </div>
  );
};

export default InterviewReady;
